import React, { useState, useRef, useMemo, useEffect } from 'react';
import type { Image as Interface, FileMetadata } from '../../design/components';
import { isValidImageSource, isFileMetadata, downloadImage } from '../shared/image';

export const Image: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'No image selected',
  hidden,
  inline,
  readonly: _readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['' as unknown as FileMetadata, () => {}];
  const [expanded, setExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValid = isValidImageSource(value);

  const displayUrl = useMemo(() => isValid ? (value as FileMetadata).url : '', [value, isValid]);

  useEffect(() => {
    return () => { if (displayUrl.startsWith('blob:')) URL.revokeObjectURL(displayUrl); };
  }, [displayUrl]);

  const handleDownload = () => {
    if (isFileMetadata(value)) downloadImage(value.url, value.name);
  };

  const processFile = async (file: File) => {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.src = url;
      await img.decode();
      setValue({
        url, name: file.name, type: file.type, size: file.size,
        modified: new Date(file.lastModified).toISOString(),
        pages: 1, width: img.naturalWidth, height: img.naturalHeight, file,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file).catch(() => {});
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file).catch(() => {});
  };

  const controlsHeight = 40;
  const previewHeight = 300;
  const containerHeight = previewHeight + controlsHeight;

  if (inline) {
    return value ? (
      <img
        src={displayUrl}
        alt={name}
        style={{ display: hidden ? 'none' : 'inline-block', width: 32, height: 32, objectFit: 'cover', borderRadius: '4px' }}
      />
    ) : null;
  }

  const canUpload = !_readonly && !disabled;

  return (
    <div style={{ marginBottom: '1rem', display: hidden ? 'none' : undefined }}>
      {name && (
        <label htmlFor={name} style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
          {name}
          {required && <span style={{ color: 'red' }}> *</span>}
        </label>
      )}

      {/* Image Preview */}
      {isValid && (
        <div
          style={{
            width: '100%',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#f5f5f5',
            overflow: expanded ? 'auto' : 'hidden',
            height: expanded ? undefined : containerHeight,
            maxHeight: expanded ? '80vh' : undefined,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {/* Controls */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 0',
              borderBottom: '1px solid #ccc',
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <button
              onClick={() => setExpanded(!expanded)}
              title={expanded ? 'Shrink' : 'Expand'}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
            >
              {expanded ? '\u229F' : '\u229E'}
            </button>

            {isFileMetadata(value) && (
              <a
                href={value.url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in new tab"
                style={{ padding: '4px', color: 'inherit' }}
              >
                {'\u2197'}
              </a>
            )}

            <button
              onClick={handleDownload}
              title="Download"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
            >
              {'\u2B07'}
            </button>

            {canUpload && (
              <button
                onClick={() => setValue('' as unknown as FileMetadata)}
                title="Remove"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
              >
                {'\u{1F5D1}'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={displayUrl}
              alt={name}
              style={{ maxWidth: '100%', maxHeight: previewHeight, objectFit: 'contain' }}
            />
          </div>
        </div>
      )}

      {/* Upload zone - empty state */}
      {!isValid && canUpload && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '96px',
            borderRadius: '4px',
            border: dragOver ? '1px dashed #1976d2' : error ? '1px dashed red' : '1px dashed #ccc',
            backgroundColor: dragOver ? '#e3f2fd' : '#f5f5f5',
            color: '#666',
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <span style={{ fontSize: '24px', marginBottom: '4px' }}>{'\u2B06'}</span>
          <span style={{ fontSize: '14px' }}>Click or drag to upload image</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      )}

      {/* Static empty state for readonly/disabled */}
      {!isValid && !canUpload && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '96px',
            borderRadius: '4px',
            border: error ? '1px solid red' : '1px solid #ccc',
            backgroundColor: '#f5f5f5',
            color: '#666',
          }}
        >
          <span style={{ marginRight: '8px' }}>{'\uD83D\uDDBC'}</span>
          <span>{placeholder}</span>
        </div>
      )}

      {(error || description) && (
        <small style={{ color: error ? 'red' : '#666', display: 'block', marginTop: '4px' }}>
          {error || description}
        </small>
      )}
    </div>
  );
};
